import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, MapPin, Mail, Phone } from "lucide-react";

/**
 * AdminSiteContact — edits the public footer / contact-page details.
 * Backing endpoints: GET /api/site-config (public) · POST /api/admin/site-config (admin).
 */
export default function AdminSiteContact() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/site-config").then((r) => setCfg({
    phone: r.data?.phone || "",
    email: r.data?.email || "",
    address: {
      line1:     r.data?.address?.line1     || "",
      line2:     r.data?.address?.line2     || "",
      city:      r.data?.address?.city      || "",
      postcode:  r.data?.address?.postcode  || "",
      country:   r.data?.address?.country   || "",
      mapsQuery: r.data?.address?.mapsQuery || "",
    },
    updated_at: r.data?.updated_at,
  }));

  useEffect(() => { load(); }, []);

  const setField = (k, v) => setCfg({ ...cfg, [k]: v });
  const setAddr  = (k, v) => setCfg({ ...cfg, address: { ...cfg.address, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/site-config", {
        phone: cfg.phone,
        email: cfg.email,
        address: cfg.address,
      });
      toast.success("Site contact details saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!cfg) return <AdminLayout><div className="p-8 text-sm text-zinc-500">Loading…</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="fl-label text-[#f47a20]">Public site</div>
        <h1 className="fl-heading text-4xl mt-2">Contact details</h1>
        <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--fl-muted)" }}>
          These values power the public footer, Contact page, WhatsApp button and appointment confirmation emails.
          Leave a field blank to fall back to the compiled defaults in <code>src/config/site.js</code>.
        </p>
        {cfg.updated_at && (
          <div className="text-[11px] mt-2" style={{ color: "var(--fl-muted-2)" }}>
            Last updated {new Date(cfg.updated_at).toLocaleString("en-GB")}
          </div>
        )}
      </div>

      <div className="fl-card fl-card-elevated p-6">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Phone (display format)" icon={<Phone size={12} />}>
            <input className="fl-input" value={cfg.phone} onChange={(e) => setField("phone", e.target.value)}
                   placeholder="+44 7435 053923" data-testid="site-phone" />
          </Field>
          <Field label="Contact email" icon={<Mail size={12} />}>
            <input type="email" className="fl-input" value={cfg.email} onChange={(e) => setField("email", e.target.value)}
                   placeholder="functional.lab25@gmail.com" data-testid="site-email" />
          </Field>
        </div>

        <div className="fl-label text-[#f47a20] mt-6 mb-2 flex items-center gap-2"><MapPin size={12} /> Address</div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Line 1">
            <input className="fl-input" value={cfg.address.line1} onChange={(e) => setAddr("line1", e.target.value)}
                   placeholder="e.g. 12 Rehabilitation Way" data-testid="site-address-line1" />
          </Field>
          <Field label="Line 2 (optional)">
            <input className="fl-input" value={cfg.address.line2} onChange={(e) => setAddr("line2", e.target.value)}
                   data-testid="site-address-line2" />
          </Field>
          <Field label="City">
            <input className="fl-input" value={cfg.address.city} onChange={(e) => setAddr("city", e.target.value)}
                   placeholder="London" data-testid="site-address-city" />
          </Field>
          <Field label="Postcode">
            <input className="fl-input" value={cfg.address.postcode} onChange={(e) => setAddr("postcode", e.target.value)}
                   placeholder="SW1A 1AA" data-testid="site-address-postcode" />
          </Field>
          <Field label="Country">
            <input className="fl-input" value={cfg.address.country} onChange={(e) => setAddr("country", e.target.value)}
                   placeholder="United Kingdom" data-testid="site-address-country" />
          </Field>
          <Field label="Maps query (for the Contact page pin)">
            <input className="fl-input" value={cfg.address.mapsQuery} onChange={(e) => setAddr("mapsQuery", e.target.value)}
                   placeholder="Functional+LAB+London" data-testid="site-address-maps" />
          </Field>
        </div>

        <div className="mt-6">
          <button className="fl-btn" onClick={save} disabled={saving} data-testid="site-save">
            <Save size={14} /> {saving ? "Saving…" : "Save contact details"}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}

function Field({ label, icon, children }) {
  return (
    <div>
      <div className="fl-label mb-1 flex items-center gap-2">{icon}{label}</div>
      {children}
    </div>
  );
}
